'use client'

import { Input } from '@/components/ui/input'
import { FieldError, FieldHelpText, FieldLabel, FieldShell, premiumControlClass } from './field-shell'
import type { BlockPropsOf, FieldMode } from './types'

/**
 * Shared Signature field — consumed by BOTH the Puck builder block
 * (`mode="preview"`) and the runtime `FormRenderer` (`mode="live"`), the same
 * single-source pattern as the other answerable fields (KTD6 — no double
 * render).
 *
 * Signature is composite, so it does not fit the generic `AnswerableFieldProps`
 * contract: it captures a typed signature (payload, keyed by block id) and,
 * when `requireSignerIdentity` is set, the signer's name + email — which the
 * renderer routes to the submission's top-level `signerName`/`signerEmail`
 * (attribution), not into the answer payload.
 */
export interface SignatureFieldProps {
  props: BlockPropsOf<'Signature'>
  mode: FieldMode
  /** Typed signature text. */
  value?: string
  onChange?: (value: string) => void
  error?: string
  /** Signer identity — only rendered/collected when `props.requireSignerIdentity`. */
  signerName?: string
  signerEmail?: string
  onSignerNameChange?: (value: string) => void
  onSignerEmailChange?: (value: string) => void
  signerNameError?: string
  signerEmailError?: string
}

export function SignatureField({
  props,
  mode,
  value,
  onChange,
  error,
  signerName,
  signerEmail,
  onSignerNameChange,
  onSignerEmailChange,
  signerNameError,
  signerEmailError
}: SignatureFieldProps) {
  const isPreview = mode === 'preview'
  const requireIdentity = Boolean(props.requireSignerIdentity)

  return (
    <FieldShell
      mode={mode}
      htmlFor={props.id}
      label={props.label}
      required={props.required}
      helpText={props.helpText}
      error={error}
      conditional={Boolean(props.showIf)}
    >
      <Input
        id={props.id}
        type="text"
        className={premiumControlClass}
        placeholder="Type your full name to sign"
        readOnly={isPreview}
        value={isPreview ? '' : typeof value === 'string' ? value : ''}
        onChange={isPreview ? undefined : e => onChange?.(e.target.value)}
      />
      {requireIdentity && (
        <div className="space-y-2 pt-1">
          <p className="text-xs text-muted-foreground">Confirm who is signing</p>
          <div className="space-y-1">
            <FieldLabel htmlFor={`${props.id}-signer-name`} label="Signer name" required />
            <Input
              id={`${props.id}-signer-name`}
              type="text"
              className={premiumControlClass}
              placeholder="Full name"
              readOnly={isPreview}
              value={isPreview ? '' : (signerName ?? '')}
              onChange={isPreview ? undefined : e => onSignerNameChange?.(e.target.value)}
            />
            <FieldError error={signerNameError} />
          </div>
          <div className="space-y-1">
            <FieldLabel htmlFor={`${props.id}-signer-email`} label="Signer email" required />
            <Input
              id={`${props.id}-signer-email`}
              type="email"
              className={premiumControlClass}
              placeholder="name@example.com"
              readOnly={isPreview}
              value={isPreview ? '' : (signerEmail ?? '')}
              onChange={isPreview ? undefined : e => onSignerEmailChange?.(e.target.value)}
            />
            <FieldError error={signerEmailError} />
          </div>
        </div>
      )}
      {isPreview && requireIdentity && <FieldHelpText helpText="Signer name + email are required on this form" />}
    </FieldShell>
  )
}
